import { useCallback } from 'react';
import { Node, Connection, OnConnect } from '@xyflow/react';
import { ResultCircleNodeData } from '../components/ResultCircleNode';
import { DraggableResultCircleNodeData } from '../components/DraggableResultCircleNode';
import { DependencyRecord } from '@mm-shared/dependencies';

/**
 * Custom hook for handling new dependency connections in React Flow
 *
 * This hook manages the creation of new dependencies when users drag between
 * result nodes in React Flow. It provides validation and prevents invalid connections.
 *
 * Key features:
 * 1. Prevents self-connections (a result cannot depend on itself)
 * 2. Prevents duplicate connections (same dependency already exists)
 * 3. Extracts objective UUID from source node for proper dependency creation
 * 4. Calls the provided addDependency function with proper parameters
 *
 * Validation logic:
 * - Ensures both source and target exist
 * - Checks for self-loops (source === target)
 * - Checks existing dependencies to prevent duplicates
 * - Finds source node to extract objective context
 *
 * @param nodes - Array of React Flow nodes (to find source node data)
 * @param localDependencies - Current dependency records for duplicate checking
 * @param addDependency - Function to create new dependency (sourceResultUuid, targetResultUuid, sourceObjectiveUuid)
 * @returns OnConnect handler function for React Flow
 */
export function useConnectHandler(
  nodes: Node[],
  localDependencies: DependencyRecord[],
  addDependency: (
    sourceResultUuid: string,
    targetResultUuid: string,
    sourceObjectiveUuid: string,
  ) => void,
) {
  return useCallback<OnConnect>(
    (connection: Connection) => {
      if (!connection.source || !connection.target) return;

      // Prevent self-connections
      if (connection.source === connection.target) {
        console.warn('Cannot create dependency: A result cannot depend on itself');
        return;
      }

      // Prevent duplicate connections
      const isDuplicate = localDependencies.some(
        (dep) =>
          dep.sourceResult.uuid === connection.source &&
          dep.targetResult.uuid === connection.target,
      );
      if (isDuplicate) {
        console.warn('Cannot create dependency: This dependency already exists');
        return;
      }

      // Find the source node to get its objective UUID
      const sourceNode = nodes.find((n) => n.id === connection.source);
      if (!sourceNode) return;

      const nodeData = sourceNode.data as ResultCircleNodeData | DraggableResultCircleNodeData;

      // Create the dependency
      addDependency(connection.source, connection.target, nodeData.objectiveUuid);
    },
    [localDependencies, addDependency, nodes],
  );
}
